import { useEffect, useState } from 'react';
import EChart from '../../components/Charts/EChart';
import { analyticsApi } from '../../services/api';
import toast from 'react-hot-toast';

export default function BehaviorTab({ dateRange }) {
  const [loading, setLoading] = useState(true);
  const [entryPatternsData, setEntryPatternsData] = useState(null);
  const [activityFrequencyData, setActivityFrequencyData] = useState(null);
  const [appLinksData, setAppLinksData] = useState(null);
  const [dailyPatternData, setDailyPatternData] = useState(null);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const getDaysFromRange = () => {
    return dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
  };

  async function loadData() {
    try {
      setLoading(true);
      const days = getDaysFromRange();

      const [entryPatterns, activityFrequency, appLinks, dailyPattern] = await Promise.all([
        analyticsApi.entryPatterns(days),
        analyticsApi.activityFrequency(days),
        analyticsApi.appLinks(days),
        analyticsApi.dailyPattern(days)
      ]);

      setEntryPatternsData(entryPatterns);
      setActivityFrequencyData(activityFrequency);
      setAppLinksData(appLinks);
      setDailyPatternData(dailyPattern);
    } catch (error) {
      console.error('Failed to load behavior analytics:', error);
      toast.error('Failed to load behavior analytics');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  const entryPatternsChart = transformEntryPatternsData(entryPatternsData);
  const activityFrequencyChart = transformActivityFrequencyData(activityFrequencyData);
  const appLinksChart = transformAppLinksData(appLinksData);
  const dailyPatternChart = transformDailyPatternData(dailyPatternData);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Entries"
          value={entryPatternsData?.total_entries || 0}
          subtitle={`In last ${dateRange}`}
          icon="📝"
        />
        <MetricCard
          title="Pee Entries"
          value={entryPatternsData?.pee_entries || 0}
          subtitle="Urination logs"
          icon="🚽"
        />
        <MetricCard
          title="Drink Entries"
          value={entryPatternsData?.drink_entries || 0}
          subtitle="Fluid intake logs"
          icon="💧"
        />
        <MetricCard
          title="Active Users"
          value={entryPatternsData?.active_users || 0}
          subtitle="Users with entries"
          icon="👥"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Entry Patterns by Hour */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Entry Patterns by Hour</h2>
          <EChart option={entryPatternsChart} height="300px" />
        </div>

        {/* Activity Frequency Distribution */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">User Activity Levels</h2>
          <EChart option={activityFrequencyChart} height="300px" />
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* App Link Usage */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">App Link Clicks</h2>
          <EChart option={appLinksChart} height="300px" />
        </div>

        {/* Daily Active User Pattern */}
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Daily Active Users</h2>
          <EChart option={dailyPatternChart} height="300px" />
        </div>
      </div>

      {/* Entry Insights */}
      {entryPatternsData && (
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Entry Insights</h2>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatItem
              label="Avg Entries/User"
              value={(entryPatternsData.average_entries_per_user || 0).toFixed(1)}
            />
            <StatItem
              label="Peak Hour"
              value={formatHour(findPeakHour(entryPatternsData.by_hour))}
            />
            <StatItem
              label="Pee/Drink Ratio"
              value={calculateRatio(entryPatternsData.pee_entries, entryPatternsData.drink_entries)}
            />
            <StatItem
              label="Active Days"
              value={dailyPatternData?.pattern?.length || 0}
            />
          </dl>
        </div>
      )}
    </div>
  );
}

function transformEntryPatternsData(data) {
  if (!data?.by_hour || data.by_hour.length === 0) {
    return {
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value', name: 'Entries' },
      series: []
    };
  }

  const sortedData = [...data.by_hour].sort((a, b) => a.hour - b.hour);

  const categories = sortedData.map(item => {
    const hour = item.hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}${ampm}`;
  });

  const entryData = sortedData.map(item => item.entry_count || 0);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const param = params[0];
        return `${param.axisValue}<br/>${param.marker}${param.seriesName}: ${param.value.toLocaleString()} entries`;
      }
    },
    xAxis: {
      type: 'category',
      data: categories,
      name: 'Hour of Day',
      nameLocation: 'middle',
      nameGap: 35,
      axisLabel: {
        rotate: 45,
        fontSize: 11
      }
    },
    yAxis: {
      type: 'value',
      name: 'Number of Entries',
      splitLine: {
        lineStyle: { type: 'dashed' }
      }
    },
    series: [
      {
        name: 'Entries',
        type: 'bar',
        data: entryData,
        itemStyle: {
          color: '#3b82f6',
          borderRadius: [4, 4, 0, 0]
        },
        barWidth: '70%'
      }
    ]
  };
}

function transformActivityFrequencyData(data) {
  if (!data?.distribution || data.distribution.length === 0) {
    return {
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value', name: 'Users' },
      series: []
    };
  }

  const categories = data.distribution.map(item => item.activity_level);
  const userData = data.distribution.map(item => item.user_count || 0);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        fontSize: 11
      }
    },
    yAxis: {
      type: 'value',
      name: 'Number of Users',
      splitLine: {
        lineStyle: { type: 'dashed' }
      }
    },
    series: [
      {
        name: 'Users',
        type: 'bar',
        data: userData,
        itemStyle: {
          color: '#10b981',
          borderRadius: [4, 4, 0, 0]
        },
        label: {
          show: true,
          position: 'top',
          formatter: (params) => params.value.toLocaleString()
        }
      }
    ]
  };
}

function transformAppLinksData(data) {
  if (!data?.by_type || data.by_type.length === 0) {
    return {
      xAxis: { type: 'value', name: 'Clicks' },
      yAxis: { type: 'category', data: [] },
      series: []
    };
  }

  const categories = data.by_type.map(item => formatLinkType(item.link_type));
  const clickData = data.by_type.map(item => item.click_count || 0);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    xAxis: {
      type: 'value',
      name: 'Number of Clicks',
      splitLine: {
        lineStyle: { type: 'dashed' }
      }
    },
    yAxis: {
      type: 'category',
      data: categories
    },
    series: [
      {
        name: 'Clicks',
        type: 'bar',
        data: clickData,
        itemStyle: {
          color: '#f59e0b',
          borderRadius: [0, 4, 4, 0]
        },
        label: {
          show: true,
          position: 'right',
          formatter: (params) => params.value.toLocaleString()
        }
      }
    ]
  };
}

function transformDailyPatternData(data) {
  if (!data?.pattern || data.pattern.length === 0) {
    return {
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value', name: 'Active Users' },
      series: []
    };
  }

  const categories = data.pattern.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });

  const activeUsers = data.pattern.map(item => item.active_users || 0);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' }
    },
    xAxis: {
      type: 'category',
      data: categories,
      boundaryGap: false,
      axisLabel: {
        rotate: 45,
        fontSize: 11
      }
    },
    yAxis: {
      type: 'value',
      name: 'Active Users',
      splitLine: {
        lineStyle: { type: 'dashed' }
      }
    },
    series: [
      {
        name: 'Active Users',
        type: 'line',
        data: activeUsers,
        smooth: true,
        itemStyle: { color: '#8b5cf6' },
        lineStyle: { width: 3 },
        symbol: 'circle',
        symbolSize: 6,
        emphasis: {
          itemStyle: {
            borderColor: '#8b5cf6',
            borderWidth: 2
          }
        }
      }
    ]
  };
}

function MetricCard({ title, value, subtitle, icon }) {
  return (
    <div className="rounded-lg bg-white p-6 shadow card-hover">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="text-3xl font-bold text-gray-900">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      <p className="text-sm text-gray-500 mt-2">{subtitle}</p>
    </div>
  );
}

function StatItem({ label, value }) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-600">{label}</dt>
      <dd className="mt-1 text-2xl font-bold text-gray-900">{value}</dd>
    </div>
  );
}

function formatHour(hour) {
  if (hour === null || hour === undefined) return 'N/A';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}${ampm}`;
}

function findPeakHour(byHour) {
  if (!byHour || byHour.length === 0) return null;
  const peak = byHour.reduce((max, item) =>
    (item.entry_count > max.entry_count) ? item : max
  );
  return peak.hour;
}

function calculateRatio(pee, drink) {
  if (!drink || drink === 0) return 'N/A';
  return (pee / drink).toFixed(2);
}

function formatLinkType(type) {
  return type.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
}
